import React, { useState, useEffect } from 'react';
import axios from 'axios';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import {
  Button,
  Container,
  TextField,
  Table,
  Modal,
  TableBody,
  TableCell,
  Box,
  Typography,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Stack,
  styled,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  InputAdornment,
  useTheme,
  Popper,
  Paper as MuiPaper,
  ClickAwayListener,
  List,
  ListItem,
  ListItemText,
  Chip,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Spellcheck as SpellcheckIcon,
  AutoFixHigh as AutoFixIcon,
} from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { useNavigate } from 'react-router-dom';
import ChevronLeft from '@mui/icons-material/KeyboardDoubleArrowLeft';
import ChevronRight from '@mui/icons-material/KeyboardDoubleArrowRight';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Comprehensive dictionary of correct department names and their common misspellings
const DEPARTMENT_CORRECTIONS = {
  // Educational/Teaching departments
  'teacher': ['techer', 'teecher', 'teachar', 'tcher', 'teachr', 'teahcer', 'tacher'],
  'teaching': ['teching', 'teacching', 'teahing'],
  'education': ['eduction', 'educaton', 'educatin', 'eduction', 'educaion'],
  'academics': ['academis', 'acadmics', 'academcs', 'academiks'],
  
  // School/University departments
  'mathematics': ['math', 'mathematic', 'maths', 'mathemetics', 'matamatics'],
  'science': ['sciense', 'scince', 'sciance', 'scinece'],
  'physics': ['phisics', 'phyics', 'physcis', 'fysics'],
  'chemistry': ['chemestry', 'chemisty', 'chemsitry', 'chemstry'],
  'biology': ['bilogy', 'biolgy', 'biolog', 'bio'],
  'history': ['histroy', 'histry', 'histry', 'hestory'],
  'geography': ['geography', 'geograpy', 'geograph', 'geogarphy'],
  'literature': ['literatur', 'litrature', 'literatue', 'litterature'],
  'english': ['engish', 'englis', 'englsh', 'inglish'],
  'computer science': ['cs', 'comp science', 'computer sci', 'computerscience'],
  
  // Administrative departments
  'administration': ['administation', 'adminstration', 'administratio', 'admin', 'admistration', 'admnistration'],
  'human resources': ['hr', 'human resource', 'human resourses', 'humen resources', 'human resoures', 'humanresource', 'human resource'],
  'finance': ['finace', 'finnance', 'finence', 'financial', 'finanace'],
  'accounting': ['accounting', 'accountig', 'acounting', 'accountin', 'accouting'],
  'marketing': ['marketting', 'markiting', 'marketng', 'margeting', 'marketin'],
  'sales': ['sale', 'saless', 'seles', 'sells'],
  'information technology': ['it', 'information tech', 'info tech', 'information technology', 'infotech', 'informtion technology'],
  'operations': ['operation', 'oprations', 'operatons', 'opertions', 'operatin'],
  'customer service': ['cust service', 'customer servise', 'customerservice', 'customer support', 'custumer service'],
  'research and development': ['rnd', 'r&d', 'research & development', 'research development', 'reseach and development', 'reserch and development'],
  'engineering': ['engeneering', 'enginering', 'enginnering', 'enigeering', 'engneering'],
  'production': ['prodution', 'producton', 'producion', 'production'],
  'logistics': ['logistcs', 'logistis', 'logisitcs', 'logisics'],
  'supply chain': ['supply chain', 'suplly chain', 'supplychain', 'suppli chain'],
  'procurement': ['procurement', 'procument', 'procuement', 'procurement'],
  'purchasing': ['purchasing', 'purchesing', 'purchacing', 'purchaseing'],
  'quality assurance': ['qa', 'quality assuramce', 'quality assurance', 'qualty assurance', 'quality asurance'],
  'quality control': ['qc', 'quality controll', 'quality control', 'quality cntrol'],
  'maintenance': ['maintenace', 'maintainance', 'maintanance', 'maintence'],
  'facilities': ['facilites', 'facillities', 'facilty', 'facilites'],
  'security': ['secuirty', 'security', 'securty', 'secutity'],
  'legal': ['legel', 'legall', 'leagal', 'legall'],
  'compliance': ['complacence', 'complience', 'complaiance', 'complacance'],
  'training': ['trainning', 'traing', 'traning', 'trainig'],
  'development': ['developement', 'devlopment', 'developmnet', 'develpment'],
  'strategy': ['strategyy', 'stratey', 'stratergy', 'strategi'],
  'planning': ['planing', 'plannig', 'plannning', 'planin'],
  'communications': ['comunications', 'communcations', 'comms', 'communictions'],
  'public relations': ['pr', 'public relation', 'pubic relations', 'publc relations'],
  'it support': ['it support', 'it supportt', 'itsupport', 'it suport'],
  'database administration': ['dba', 'database admin', 'data base admin', 'databese administration'],
  'network administration': ['network admin', 'net admin', 'network administation', 'network administrtion'],
  'technical support': ['tech support', 'technical support', 'techsupport', 'technicl support'],
  'business development': ['bussiness development', 'business dev', 'biz dev', 'business developement'],
  'product management': ['product mgmt', 'product managment', 'product management', 'prod management'],
  'project management': ['project mgmt', 'project managment', 'project management', 'proj management'],
  'vendor management': ['vendor mgmt', 'vendor managment', 'vender management'],
  'risk management': ['risk mgmt', 'risk managment', 'riks management'],
  'asset management': ['asset mgmt', 'asset managment', 'assets management'],
  'facility management': ['facility mgmt', 'facility managment', 'facilites management'],
  
  // Medical/Healthcare departments
  'medical': ['medicl', 'medcal', 'medicall', 'medicle'],
  'nursing': ['nurising', 'nursin', 'nursng', 'nurcing'],
  'pharmacy': ['phamacy', 'pharmcy', 'phamracy', 'pharmecy'],
  'laboratory': ['lab', 'laboratry', 'labratory', 'labaoratory'],
  'radiology': ['radiology', 'radioology', 'radiologi', 'raiology'],
  
  // Legal departments
  'law': ['low', 'laaw', 'lwa'],
  'compliance and legal': ['compliance legal', 'complience legal', 'compliance & legal'],
  
  // Construction/Engineering departments
  'civil engineering': ['civil eng', 'civil engeneering', 'civil engg'],
  'mechanical engineering': ['mech eng', 'mechanical eng', 'mech engineering'],
  'electrical engineering': ['elec eng', 'electrical eng', 'electrical engg'],
  'architecture': ['architechture', 'archtecture', 'architexture', 'arcitecture'],
  
  // Hospitality departments
  'hospitality': ['hospitatily', 'hospitlity', 'hospitaliy'],
  'housekeeping': ['house keeping', 'housekeepin', 'housekeping'],
  'food and beverage': ['f&b', 'food & beverage', 'food beverage', 'fb'],
  
  // Retail departments
  'retail': ['retial', 'retaill', 'retal'],
  'merchandising': ['merchendising', 'merchandising', 'merchandising'],
  'inventory': ['inventry', 'inventoy', 'invetory'],
  
  // Common misspellings for standard words
  'management': ['managment', 'mangement', 'managemnt', 'manageent'],
  'department': ['departmnt', 'dept', 'deprtment', 'departement', 'dapartment'],
  'services': ['servises', 'servics', 'serices', 'servces'],
  'support': ['suport', 'suppot', 'spport', 'suppoort'],
  'coordination': ['coordinaton', 'coordiantion', 'co-ordination'],
  'supervision': ['supervison', 'supervition', 'supervisin'],
  'leadership': ['leadeship', 'leardship', 'leadrship'],
};


// Flatten the dictionary for quick lookup
const SPELLING_SUGGESTIONS = {};
Object.entries(DEPARTMENT_CORRECTIONS).forEach(([correct, misspellings]) => {
  misspellings.forEach(misspelling => {
    SPELLING_SUGGESTIONS[misspelling.toLowerCase()] = correct;
  });
  // Add the correct word itself
  SPELLING_SUGGESTIONS[correct.toLowerCase()] = correct;
});

// Common department name variations
const COMMON_DEPARTMENT_PREFIXES = ['senior', 'junior', 'associate', 'assistant', 'deputy', 'chief', 'head', 'lead'];

// Function to calculate Levenshtein distance for fuzzy matching
const levenshteinDistance = (str1, str2) => {
  const track = Array(str2.length + 1).fill(null).map(() =>
    Array(str1.length + 1).fill(null));
  for (let i = 0; i <= str1.length; i += 1) {
    track[0][i] = i;
  }
  for (let j = 0; j <= str2.length; j += 1) {
    track[j][0] = j;
  }
  for (let j = 1; j <= str2.length; j += 1) {
    for (let i = 1; i <= str1.length; i += 1) {
      const indicator = str1[i - 1] === str2[j - 1] ? 0 : 1;
      track[j][i] = Math.min(
        track[j][i - 1] + 1,
        track[j - 1][i] + 1,
        track[j - 1][i - 1] + indicator,
      );
    }
  }
  return track[str2.length][str1.length];
};

// Enhanced function to get spelling suggestions with better matching
const getEnhancedSpellingSuggestions = (word) => {
  if (!word || word.length < 2) return [];
  
  const lowerWord = word.toLowerCase().trim();
  const suggestions = new Map(); // Use Map to store suggestion with score
  
  // 1. Check direct mapping in dictionary
  if (SPELLING_SUGGESTIONS[lowerWord]) {
    suggestions.set(SPELLING_SUGGESTIONS[lowerWord], 100);
  }
  
  // 2. Check for partial matches (word contains part of dictionary word or vice versa)
  Object.keys(SPELLING_SUGGESTIONS).forEach(dictWord => {
    if (dictWord.includes(lowerWord) && dictWord.length > lowerWord.length + 1) {
      const score = 85;
      if (!suggestions.has(SPELLING_SUGGESTIONS[dictWord]) || suggestions.get(SPELLING_SUGGESTIONS[dictWord]) < score) {
        suggestions.set(SPELLING_SUGGESTIONS[dictWord], score);
      }
    } else if (lowerWord.includes(dictWord) && lowerWord.length > dictWord.length + 1) {
      const score = 80;
      if (!suggestions.has(SPELLING_SUGGESTIONS[dictWord]) || suggestions.get(SPELLING_SUGGESTIONS[dictWord]) < score) {
        suggestions.set(SPELLING_SUGGESTIONS[dictWord], score);
      }
    }
  });
  
  // 3. Fuzzy matching with Levenshtein distance
  Object.keys(SPELLING_SUGGESTIONS).forEach(dictWord => {
    if (Math.abs(dictWord.length - lowerWord.length) <= 3) {
      const distance = levenshteinDistance(lowerWord, dictWord);
      const maxDistance = Math.max(1, Math.floor(Math.min(lowerWord.length, dictWord.length) / 2.5));
      
      if (distance <= maxDistance) {
        const score = Math.max(0, 100 - (distance / maxDistance) * 30);
        const correctWord = SPELLING_SUGGESTIONS[dictWord];
        if (!suggestions.has(correctWord) || suggestions.get(correctWord) < score) {
          suggestions.set(correctWord, score);
        }
      }
    }
  });
  
  // 4. Check for common typos (character frequency and position)
  Object.entries(SPELLING_SUGGESTIONS).forEach(([dictWord, correctWord]) => {
    if (Math.abs(dictWord.length - lowerWord.length) <= 2) {
      // Check character frequency
      const freqLower = {};
      const freqDict = {};
      for (let char of lowerWord) freqLower[char] = (freqLower[char] || 0) + 1;
      for (let char of dictWord) freqDict[char] = (freqDict[char] || 0) + 1;
      
      let commonChars = 0;
      for (let char in freqLower) {
        if (freqDict[char]) {
          commonChars += Math.min(freqLower[char], freqDict[char]);
        }
      }
      
      const maxLen = Math.max(dictWord.length, lowerWord.length);
      const freqScore = (commonChars / maxLen) * 100;
      
      if (freqScore > 60 && !suggestions.has(correctWord)) {
        suggestions.set(correctWord, freqScore);
      }
    }
  });
  
  // 5. Check for common words like "teacher" and similar
  const commonWords = {
    'teacher': ['techer', 'teachar', 'tcher', 'teachr'],
    'manager': ['manger', 'maneger'],
    'assistant': ['assisstant', 'assitent'],
    'engineer': ['enginner', 'engeneer'],
    'developer': ['developr', 'devloper'],
    'director': ['directer'],
    'supervisor': ['superviser']
  };
  
  Object.entries(commonWords).forEach(([correct, misspellings]) => {
    if (misspellings.includes(lowerWord) || levenshteinDistance(lowerWord, correct) <= 2) {
      if (!suggestions.has(correct)) {
        suggestions.set(correct, 90);
      }
    }
  });
  
  // Convert to array and sort by score (highest first)
  const sortedSuggestions = Array.from(suggestions.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([suggestion]) => suggestion)
    .slice(0, 5); // Limit to top 5 suggestions
  
  // Capitalize each word in suggestion
  return sortedSuggestions.map(suggestion => 
    suggestion.split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ')
  );
};

const CustomTableScrollbar = styled('div')(({ theme }) => ({
  '&::-webkit-scrollbar': {
    height: '6px',
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'} transparent`,
}));

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const Department = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOptions = [5, 10, 25, 50];
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [showUserGuideModal, setShowUserGuideModal] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddDepartmentModal, setShowAddDepartmentModal] = useState(false);
  const [showEditDepartmentModal, setShowEditDepartmentModal] = useState(false);
  const [departmentName, setDepartmentName] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState({ deptId: null, departmentName: '' });
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [departments, setDepartments] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Spelling suggestion states
  const [suggestions, setSuggestions] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [currentWord, setCurrentWord] = useState('');
  
  // Suggestion-applied snackbar state
  const [showAutoCorrectSnackbar, setShowAutoCorrectSnackbar] = useState(false);
  const [autoCorrectMessage, setAutoCorrectMessage] = useState('');

  // Fetch all departments
  const fetchDepartments = async () => {
    setLoading(true);
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_BASE_URL}/api/master/departments/`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      const mappedDepartments = response.data.map(dept => ({
        deptId: dept.id,
        departmentName: dept.name,
      }));
      setDepartments(mappedDepartments);
      setFilteredData(mappedDepartments);
    } catch (err) {
      setError(err.message);
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to fetch departments.');
      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle department name change - shows suggestions only, never changes the text
  const handleDepartmentNameChange = (e) => {
    const value = e.target.value;
    setDepartmentName(value);

    if (value && value.trim().length > 1) {
      // Get the current word being typed (last word)
      const words = value.split(' ');
      const lastWord = words[words.length - 1];
      setCurrentWord(lastWord);

      if (lastWord.length >= 2) {
        // Get suggestions for the last word, hiding ones identical to what the user typed
        const suggestionsList = getEnhancedSpellingSuggestions(lastWord).filter(
          (s) => s.toLowerCase() !== lastWord.toLowerCase()
        );

        if (suggestionsList.length > 0) {
          setSuggestions(suggestionsList);
          setAnchorEl(e.currentTarget);
          setShowSuggestions(true);
          return;
        }
      }
    }
    setShowSuggestions(false);
    setSuggestions([]);
  };
  
  // Apply a specific suggestion (only when the user clicks it)
  const applySuggestion = (suggestion) => {
    const words = departmentName.split(' ');
    words[words.length - 1] = suggestion;
    const newText = words.join(' ');
    setDepartmentName(newText);
    setShowSuggestions(false);
    setSuggestions([]);
    setAutoCorrectMessage(`✓ Applied suggestion: "${currentWord}" → "${suggestion}"`);
    setShowAutoCorrectSnackbar(true);
    setTimeout(() => setShowAutoCorrectSnackbar(false), 3000);
  };
  
  const handleCloseSuggestions = () => {
    setShowSuggestions(false);
    setSuggestions([]);
  };

  // Add a new department
  const handleSave = async (e) => {
    e.preventDefault();
    
    // Use the text exactly as the user typed it
    const finalName = departmentName.trim();
    
    if (!finalName) {
      setSnackbarSeverity('error');
      setSnackbarMessage('Department name is required.');
      setOpenSnackbar(true);
      return;
    }
    
    // Check for duplicate department
    const isDuplicate = departments.some(
      dept => dept.departmentName.toLowerCase() === finalName.toLowerCase()
    );
    
    if (isDuplicate) {
      setSnackbarSeverity('error');
      setSnackbarMessage('This department already exists!');
      setOpenSnackbar(true);
      return;
    }
    
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await axios.post(
        `${API_BASE_URL}/api/master/departments/`,
        { name: finalName },
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );
      const newDepartment = {
        deptId: response.data.id,
        departmentName: response.data.name,
      };
      setDepartments([...departments, newDepartment]);
      setFilteredData([...filteredData, newDepartment]);
      setShowAddDepartmentModal(false);
      setSnackbarMessage('Department added successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      setDepartmentName('');
    } catch (err) {
      setError(err.message);
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to add department.');
      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        navigate('/login');
      }
    }
  };

  // Delete a department
  const handleDelete = async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      await axios.delete(`${API_BASE_URL}/api/master/departments/${selectedDepartment.deptId}/`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });
      const updatedDepartments = departments.filter((dept) => dept.deptId !== selectedDepartment.deptId);
      setDepartments(updatedDepartments);
      setFilteredData(updatedDepartments);
      setShowDeleteModal(false);
      setSnackbarMessage('Department deleted successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
    } catch (err) {
      setError(err.message);
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to delete department.');
      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        navigate('/login');
      }
    }
  };

  // Edit a department
  const handleUpdate = async () => {
    // Use the text exactly as the user typed it
    const finalName = departmentName.trim();
    
    if (!finalName) {
      setSnackbarSeverity('error');
      setSnackbarMessage('Department name is required.');
      setOpenSnackbar(true);
      return;
    }
    
    // Check for duplicate department (excluding current one)
    const isDuplicate = departments.some(
      dept => dept.departmentName.toLowerCase() === finalName.toLowerCase() && 
               dept.deptId !== selectedDepartment.deptId
    );
    
    if (isDuplicate) {
      setSnackbarSeverity('error');
      setSnackbarMessage('This department already exists!');
      setOpenSnackbar(true);
      return;
    }
    
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await axios.put(
        `${API_BASE_URL}/api/master/departments/${selectedDepartment.deptId}/`,
        { name: finalName },
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );
      const updatedDepartment = {
        deptId: response.data.id,
        departmentName: response.data.name,
      };
      const updatedDepartments = departments.map((dept) =>
        dept.deptId === selectedDepartment.deptId ? updatedDepartment : dept
      );
      setDepartments(updatedDepartments);
      setFilteredData(updatedDepartments);
      setShowEditDepartmentModal(false);
      setSnackbarMessage('Department updated successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      setDepartmentName('');
    } catch (err) {
      setError(err.message);
      setSnackbarSeverity('error');
      setSnackbarMessage('Failed to update department.');
      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        navigate('/login');
      }
    }
  };

  // Open Add Department Modal
  const handleAddDepartment = () => {
    setDepartmentName('');
    setShowAddDepartmentModal(true);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  // Open Edit Department Modal
  const handleEdit = (department) => {
    setSelectedDepartment(department);
    setDepartmentName(department.departmentName);
    setShowEditDepartmentModal(true);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  // Open Delete Confirmation Modal
  const handleShowDeleteModal = (deptId, departmentName) => {
    setSelectedDepartment({ deptId, departmentName });
    setShowDeleteModal(true);
  };

  // Close Delete Confirmation Modal
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  // Reset Add Department Form
  const handleReset = () => {
    setDepartmentName('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  // Close Edit Department Modal
  const handleCancel = () => {
    setShowEditDepartmentModal(false);
    setDepartmentName('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  // Handle Page Change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle Rows Per Page Change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  // Fetch departments on component mount
  useEffect(() => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) {
      navigate('/login');
    } else {
      fetchDepartments();
    }
  }, [navigate]);

  // Filter departments based on search term
  useEffect(() => {
    setFilteredData(
      departments.filter((dept) =>
        dept.departmentName.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm, departments]);

  // Paginate data
  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <Container>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          p: 2,
          boxShadow: 'none',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
          <TextField
            variant="outlined"
            placeholder="Search by Department Name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{
                      color: theme.palette.primary.main,
                      marginLeft: 1.5,
                      fontSize: 24,
                    }}
                  />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm('')}
                    sx={{
                      color: "grey",
                      marginRight: 1,
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                borderRadius: '30px',
                backgroundColor: theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.15)'
                  : 'rgba(255, 255, 255, 1)',
                boxShadow: theme.palette.mode === 'dark'
                  ? '0 2px 8px rgba(0, 0, 0, 0.3)'
                  : '0 2px 8px rgba(0, 0, 0, 0.1)',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
                },
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.2)'
                    : 'rgba(240, 240, 240, 1)',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 2px 12px rgba(0, 0, 0, 0.4)'
                    : '0 2px 12px rgba(0, 0, 0, 0.15)',
                },
                '&.Mui-focused': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.25)'
                    : 'rgba(255, 255, 255, 1)',
                  boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: `2px solid ${theme.palette.primary.main}`,
                  },
                },
                paddingLeft: 1.5,
                height: '50px',
                transition: 'all 0.3s ease',
              },
            }}
            sx={{
              width: 350,
              maxWidth: '100%',
              '& .MuiInputBase-input': {
                padding: '10px 14px',
                color: theme.palette.text.primary,
                fontWeight: 500,
              },
            }}
          />
          <Button
            variant="contained"
            onClick={handleAddDepartment}
            sx={{
              ml: 2,
              width: '170px',
              borderRadius: '10px',
              height: '50px',
              padding: '10px 0',
              fontWeight: 'bold',
              backgroundColor: theme.palette.primary.main,
              '&:hover': { backgroundColor: theme.palette.primary.dark },
            }}
            startIcon={<AddIcon />}
          >
            ADD DEPARTMENT
          </Button>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <IconButton
            onClick={() => setShowUserGuideModal(true)}
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              minWidth: 0,
            }}
          >
            <InfoOutlineIcon sx={{ fontSize: 30 }} />
          </IconButton>
        </Box>
      </Box>

      <Paper
        elevation={3}
        sx={{
          paddingBottom: 0,
          maxWidth: '100%',
          boxSizing: 'border-box',
          mx: 'auto',
          backgroundColor: theme.palette.background.paper,
          borderRadius: '16px',
        }}
      >
        <CustomTableScrollbar sx={{ width: '100%', overflowX: 'auto' }}>
          <TableContainer sx={{ width: '100%', borderRadius: '16px' }}>
            <Table
              sx={{
                '& .MuiTableHead-root .MuiTableCell-root': {
                  fontWeight: 'bold',
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Department Name</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: theme.palette.primary.dark, fontSize: '1.1rem' }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : paginatedData.length > 0 ? (
                  paginatedData.map((dept, index) => (
                    <TableRow
                      key={dept.deptId}
                      sx={{
                        backgroundColor: index % 2 === 0 ? theme.palette.background.paper : theme.palette.background.paper,
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        },
                      }}
                    >
                      <TableCell sx={{ fontSize: '1rem' }}>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                      <TableCell sx={{ fontSize: '1rem' }}>{dept.departmentName}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          onClick={() => handleEdit(dept)}
                          sx={{
                            color: theme.palette.primary.light,
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleShowDeleteModal(dept.deptId, dept.departmentName)}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow sx={{
                    backgroundColor: theme.palette.background.paper,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}>
                    <TableCell colSpan={3} align="center" sx={{ fontSize: '1rem', padding: '20px', wordSpacing: 7 }}>
                      No Data Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CustomTableScrollbar>
      </Paper>

      {filteredData.length > 0 && (
        <Box
          sx={{
            maxWidth: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 1,
            p: 2,
            mx: 'auto',
            pt: 2,
          }}
        >
          <Typography variant="body2" color={theme.palette.primary.main}>
            Page <strong>{page}</strong> of <strong>{Math.ceil(filteredData.length / rowsPerPage)}</strong>
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 1}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <ChevronLeft />
            </IconButton>
            {[...Array(Math.ceil(filteredData.length / rowsPerPage))].map((_, index) => (
              <IconButton
                key={index + 1}
                onClick={() => handleChangePage(null, index + 1)}
                sx={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  backgroundColor: page === index + 1 ? theme.palette.primary.main : 'transparent',
                  color: page === index + 1 ? theme.palette.primary.contrastText : theme.palette.text.primary,
                  '&:hover': {
                    backgroundColor: page === index + 1 ? theme.palette.primary.dark : theme.palette.action.hover,
                  },
                }}
              >
                {index + 1}
              </IconButton>
            ))}
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(filteredData.length / rowsPerPage)}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <ChevronRight />
            </IconButton>
            <FormControl size="small" sx={{ minWidth: 100, ml: 1 }}>
              <InputLabel id="rows-per-page-label" sx={{ color: theme.palette.text.primary }}>
                Rows
              </InputLabel>
              <Select
                labelId="rows-per-page-label"
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                label="Rows"
                sx={{
                  height: '36px',
                  color: theme.palette.text.primary,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.text.primary,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                {rowsPerPageOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      )}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Suggestion-applied notification snackbar */}
      <Snackbar
        open={showAutoCorrectSnackbar}
        autoHideDuration={4000}
        onClose={() => setShowAutoCorrectSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="info" sx={{ width: '100%', backgroundColor: theme.palette.info.main }}>
          <SpellcheckIcon sx={{ mr: 1, fontSize: 20, verticalAlign: 'middle' }} /> {autoCorrectMessage}
        </Alert>
      </Snackbar>

      {/* Delete Modal */}
      <Modal open={showDeleteModal} onClose={handleCloseDeleteModal} BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
            <Typography id="delete-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Delete Confirmation ⚠️
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography id="delete-modal-description" sx={{ mb: 2 }}>
              Are you sure you want to delete <strong>{selectedDepartment.departmentName}</strong>?
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={handleCloseDeleteModal} variant="outlined" sx={{ mr: 1 }} data-app-cancel="true">
                Cancel
              </Button>
              <Button variant="contained" color="error" onClick={handleDelete}>
                Yes, Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Add Department Modal */}
      <Modal open={showAddDepartmentModal} onClose={() => setShowAddDepartmentModal(false)} BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 450,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
              Add Department
            </Typography>
            <IconButton onClick={() => setShowAddDepartmentModal(false)} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Box sx={{ position: 'relative' }}>
              <TextField
                label="Department Name"
                variant="outlined"
                fullWidth
                value={departmentName}
                onChange={handleDepartmentNameChange}
                FormHelperTextProps={{
                  sx: {
                    color: theme.palette.mode === 'dark' ? theme.palette.text.primary : 'rgba(0, 0, 0, 0.6)',
                    fontWeight: 500
                  }
                }}
                helperText={
                  <span>
                    <SpellcheckIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                    Suggestions appear as you type. Click one to use it, or keep your own spelling.
                  </span>
                }
                sx={{
                  mb: 3,
                  '& .MuiInputLabel-root': {
                    color: theme.palette.text.primary,
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: theme.palette.primary.main,
                  }
                }}
              />
              
              {/* Suggestions Popper */}
              <Popper
                open={showSuggestions}
                anchorEl={anchorEl}
                placement="bottom-start"
                sx={{ zIndex: 1300, width: anchorEl?.clientWidth }}
              >
                <ClickAwayListener onClickAway={handleCloseSuggestions}>
                  <MuiPaper elevation={3} sx={{ mt: 0.5, maxHeight: 250, overflow: 'auto' }}>
                    <List dense>
                      <ListItem sx={{ backgroundColor: theme.palette.action.hover }}>
                        <ListItemText 
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AutoFixIcon fontSize="small" color="primary" />
                              <Typography variant="body2" fontWeight="bold" color="primary">
                                Spelling Suggestions for "{currentWord}"
                              </Typography>
                            </Box>
                          }
                        />
                      </ListItem>
                      {suggestions.map((suggestion, idx) => (
                        <ListItem 
                          key={idx} 
                          button 
                          onClick={() => applySuggestion(suggestion)}
                          sx={{
                            '&:hover': {
                              backgroundColor: theme.palette.action.selected,
                            },
                          }}
                        >
                          <ListItemText 
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip 
                                  label={idx === 0 ? "Best Match" : "Alternative"} 
                                  size="small" 
                                  color={idx === 0 ? "primary" : "default"}
                                  sx={{ fontSize: '0.7rem' }}
                                />
                                <Typography variant="body2">{suggestion}</Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </MuiPaper>
                </ClickAwayListener>
              </Popper>
            </Box>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleSave}
                sx={{
                  mr: 2,
                  backgroundColor: theme.palette.primary.main,
                  '&:hover': { backgroundColor: theme.palette.primary.dark }
                }}
                disabled={!departmentName}
              >
                SAVE
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleReset}
              >
                RESET
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
{/* Edit Department Modal */}
<Modal
  open={showEditDepartmentModal}
  onClose={handleCancel}
  BackdropProps={{
    style: { backgroundColor: "rgba(0, 0, 0, 0.8)" },
  }}
>
  <Box
    sx={{
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: 450,
      bgcolor: "background.paper",
      color: "text.primary",
      borderRadius: 2,
      boxShadow: 24,
      overflow: "hidden",
    }}
  >
    {/* Header */}
    <Box
      sx={{
          color: theme.palette.primary.contrastText,
        padding: "12px 16px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          color: theme.palette.primary.contrastText,
          fontWeight: "bold",
        }}
      >
        Edit Department
      </Typography>

      <IconButton
        onClick={handleCancel}
        sx={{
          color: theme.palette.primary.contrastText,
        }}
      >
        <CloseIcon />
      </IconButton>
    </Box>

    {/* Body */}
    <Box sx={{ padding: "20px" }}>
      <Box sx={{ position: "relative" }}>
        <TextField
          label="Department Name"
          variant="outlined"
          fullWidth
          value={departmentName}
          onChange={handleDepartmentNameChange}
          helperText={
            <Box
              component="span"
              sx={{
                display: "flex",
                alignItems: "center",
          color: theme.palette.primary.contrastText,
              }}
            >
              <SpellcheckIcon
                sx={{
                  fontSize: 14,
                  mr: 0.5,
          color: theme.palette.primary.contrastText,
                }}
              />
              Suggestions appear as you type. Click one to use it, or keep your own spelling.
            </Box>
          }
          sx={{
            mb: 3,
            "& .MuiInputLabel-root": {
              color: theme.palette.text.primary,
            },
            "& .MuiInputLabel-root.Mui-focused": {
              color: theme.palette.primary.main,
            },
            "& .MuiOutlinedInput-root": {
              color: theme.palette.text.primary,
              "& fieldset": {
                borderColor: theme.palette.divider,
              },
              "&:hover fieldset": {
                borderColor: theme.palette.primary.main,
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
        />

        {/* Suggestions Popper */}
        <Popper
          open={showSuggestions}
          anchorEl={anchorEl}
          placement="bottom-start"
          sx={{
            zIndex: 1300,
            width: anchorEl?.clientWidth,
          }}
        >
          <ClickAwayListener onClickAway={handleCloseSuggestions}>
            <MuiPaper
              elevation={3}
              sx={{
                mt: 0.5,
                maxHeight: 250,
                overflow: "auto",
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <List dense sx={{ p: 0 }}>
                {/* Suggestions Header */}
                <ListItem
                  sx={{
                    backgroundColor: theme.palette.action.hover,
                  }}
                >
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <AutoFixIcon
                          fontSize="small"
                          sx={{
          color: theme.palette.primary.contrastText,
                          }}
                        />

                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          sx={{
                              color: theme.palette.primary.main,
                          }}
                        >
                          Spelling Suggestions for "{currentWord}"
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>

                {/* Suggestions */}
                {suggestions.map((suggestion, idx) => (
                  <ListItem
                    key={idx}
                    button
                    onClick={() => applySuggestion(suggestion)}
                    sx={{
                      color: theme.palette.text.primary,
                      "&:hover": {
                        backgroundColor: theme.palette.action.selected,
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Chip
                            label={
                              idx === 0 ? "Best Match" : "Alternative"
                            }
                            size="small"
                            color={idx === 0 ? "primary" : "default"}
                            sx={{
                              fontSize: "0.7rem",
                            }}
                          />

                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.primary,
                            }}
                          >
                            {suggestion}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </MuiPaper>
          </ClickAwayListener>
        </Popper>
      </Box>

      {/* Buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          variant="contained"
          onClick={handleUpdate}
          sx={{
            mr: 2,
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            "&:hover": {
              backgroundColor: theme.palette.primary.dark,
            },
          }}
          disabled={!departmentName}
        >
          UPDATE
        </Button>

        <Button
          variant="contained"
          color="error"
          onClick={handleCancel}
        >
          CANCEL
        </Button>
      </Box>
    </Box>
  </Box>
</Modal>
      {/* User Guide Modal */}
      <Modal
        open={showUserGuideModal}
        onClose={() => setShowUserGuideModal(false)}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 900,
            bgcolor: 'background.paper',
            borderRadius: '16px',
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              padding: '12px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'relative',
            }}
          >
            <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
              Department Management - User Guide
            </Typography>
            <IconButton
              onClick={() => setShowUserGuideModal(false)}
              sx={{ color: '#fff', position: 'absolute', right: 14 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <CustomTableScrollbar sx={{ maxHeight: '70vh', overflowY: 'auto', padding: '20px' }}>
            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}
            >
              💡 Spelling Suggestions
            </Typography>
            <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
              <li>
                <strong>Intelligent Spelling Suggestions:</strong> The system detects common department name spelling mistakes and suggests corrections. Nothing is changed unless you choose it.
              </li>
              <li>
                <strong>Example:</strong> If you type "<strong>adminstration</strong>", it will suggest "<strong>Administration</strong>".
              </li>
              <li>
                <strong>Real-time Suggestions:</strong> As you type, a dropdown appears with the best matching suggestions.
              </li>
              <li>
                <strong>Best Match Indicator:</strong> The top suggestion is marked as "Best Match" for quick selection.
              </li>
              <li>
                <strong>One-Click Selection:</strong> Click any suggestion to use it, or ignore the dropdown to keep your own spelling.
              </li>
              <li>
                <strong>Visual Feedback:</strong> A notification confirms when a suggestion is applied.
              </li>
            </ul>
            
            <Typography variant="body2" sx={{ mt: 2, p: 2, bgcolor: theme.palette.info.light + '20', borderRadius: 2 }}>
              💡 <strong>Pro Tip:</strong> The suggestions understand common department names. For example:
              <br />• "adminstration" → "Administration"
              <br />• "hr" → "Human Resources"
              <br />• "marketting" → "Marketing"
              <br />• "it" → "Information Technology"
              <br />• "accouting" → "Accounting"
            </Typography>

            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
            >
              📋 Common Suggestion Examples
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, mb: 2 }}>
              <Box>
                <Typography variant="subtitle2" fontWeight="bold">Common Typos → Suggested</Typography>
                <ul style={{ marginLeft: '20px', lineHeight: '1.6' }}>
                  <li>adminstration → Administration</li>
                  <li>hr → Human Resources</li>
                  <li>marketting → Marketing</li>
                  <li>it → Information Technology</li>
                  <li>accouting → Accounting</li>
                  <li>finace → Finance</li>
                </ul>
              </Box>
              <Box>
                <Typography variant="subtitle2" fontWeight="bold">More Examples</Typography>
                <ul style={{ marginLeft: '20px', lineHeight: '1.6' }}>
                  <li>engeneering → Engineering</li>
                  <li>logistcs → Logistics</li>
                  <li>procument → Procurement</li>
                  <li>maintenace → Maintenance</li>
                  <li>qa → Quality Assurance</li>
                  <li>rnd → Research and Development</li>
                </ul>
              </Box>
            </Box>

            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
            >
              ➤ Step-by-Step Guide: Adding a Department
            </Typography>
            <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
              <li>Click the <strong>"ADD DEPARTMENT"</strong> button.</li>
              <li>Type the <strong>Department Name</strong> (e.g., "human resourses").</li>
              <li>Watch as suggestions appear below the field.</li>
              <li>Click a <strong>suggestion</strong> to use it, or keep what you typed.</li>
              <li>Review the text and click <strong>"SAVE"</strong>.</li>
            </ul>

            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
            >
              ➤ Editing Existing Departments
            </Typography>
            <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
              <li>Click the <strong>Edit icon</strong> (pencil) next to any department.</li>
              <li>The same spelling suggestions are available in the edit dialog.</li>
              <li>Make your changes, pick a suggestion if you want one, and click <strong>"UPDATE"</strong>.</li>
            </ul>

            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
            >
              ➤ Deleting a Department
            </Typography>
            <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
              <li>Click the <strong>Delete icon</strong> (trash) next to any department.</li>
              <li>Confirm the deletion in the dialog.</li>
            </ul>

            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
            >
              ➤ Searching for Departments
            </Typography>
            <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
              <li>Use the search bar to filter departments by name.</li>
              <li>The search is case-insensitive and updates in real-time.</li>
            </ul>
          </CustomTableScrollbar>
        </Box>
      </Modal>

    </Container>
  );
};

export default Department;